/**
 * Temporary UI guard for tenant users.
 *
 * Callers pass the retailer names already shown on the row. A tenant may act
 * only when every one of those names is a retailer root assigned to their account
 * (the bottom-nav retailer options, excluding All Retailers).
 */
import {
  concreteRetailerSelectionOptions,
  resolveRetailerSelectionOptions,
} from "./retailerSelection";

/** Set to false to turn this guard off without removing call sites. */
export const TEMP_TENANT_RETAILER_RECORD_GUARD = true;

export const TENANT_RETAILER_SCOPE_DENIED_TOOLTIP = "No access";

const normalizeUserType = (userType) =>
  typeof userType === "string" ? userType.toLowerCase().trim() : "";

const normalizeRetailerName = (name) =>
  typeof name === "string" ? name.trim().toLowerCase() : "";

/**
 * @param {string} userType logged-in user type
 * @param {string[]} retailerNames names on this row, as the listing already shows them
 * @param {object} stateData Redux userSelections, used to read the tenant's assigned roots
 * @returns {boolean} true when the user may use edit/action controls on this row
 */
export const tenantRetailerScopeAllowsOperation = (userType, retailerNames, stateData) => {
  if (TEMP_TENANT_RETAILER_RECORD_GUARD !== true) {
    return true;
  }

  if (normalizeUserType(userType) !== "tenant") {
    return true;
  }

  const assignedRoots = concreteRetailerSelectionOptions(
    resolveRetailerSelectionOptions(stateData)
  );
  if (assignedRoots.length === 0) {
    return false;
  }

  const allowedNames = new Set(
    assignedRoots.map((option) => normalizeRetailerName(option.label))
  );
  const namesOnRow = (Array.isArray(retailerNames) ? retailerNames : [])
    .map(normalizeRetailerName)
    .filter((name) => name !== "");

  // No names on the row means we cannot prove the retailers match, so deny.
  if (namesOnRow.length === 0) {
    return false;
  }

  return namesOnRow.every((name) => allowedNames.has(name));
};
